import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { posthog } from 'posthog-js';
import { useBooleanFeature } from '../../hooks/useFeatures';
import { useUserTags } from '../../hooks/useTags';
import { pushModal } from '../globalModals/globalModalsSlice';
import { RootState } from '../../reducers';

// Map use case options to feature IDs for tooltip targeting
export const USE_CASE_FEATURE_MAP: Record<string, string> = {
  'AI-generated summaries': 'ai-summary',
  'Searchable transcripts': 'transcript',
  'Automatic note-taking': 'notes',
  'Chat with my recordings': 'ai-chat',
  'YouTube video summaries': 'youtube-summary',
  'All of the above': 'all',
};

// Page types for tooltip targeting
export type TooltipPage = 'dashboard' | 'video';

// Tooltip step configuration
export interface TooltipStepConfig {
  elementId: string;
  page: TooltipPage;
  navigateToDemo?: boolean; // If true, navigate to demo video after this step
}

// Feature element IDs that tooltips can point to - page-aware
// Features can have multiple steps (e.g., first point to demo, then to specific feature)
export const FEATURE_TOOLTIP_STEPS: Record<string, TooltipStepConfig[]> = {
  // AI summary: Step 1 = demo video on dashboard, Step 2 = summary tab on video page
  'ai-summary': [
    {
      elementId: 'feature-demo-video',
      page: 'dashboard',
      navigateToDemo: true,
    },
    { elementId: 'feature-summary-tab', page: 'video' },
  ],
  // Transcript: Step 1 = demo video on dashboard, Step 2 = transcript tab on video page
  transcript: [
    {
      elementId: 'feature-demo-video',
      page: 'dashboard',
      navigateToDemo: true,
    },
    { elementId: 'feature-transcript-tab', page: 'video' },
  ],
  // Single-step features
  notes: [{ elementId: 'feature-record-audio', page: 'dashboard' }],
  'ai-chat': [{ elementId: 'feature-ai-chat', page: 'dashboard' }],
  'youtube-summary': [{ elementId: 'feature-import-url', page: 'dashboard' }],
  all: [
    {
      elementId: 'feature-demo-video',
      page: 'dashboard',
      navigateToDemo: true,
    },
  ],
};

// Legacy export for backward compatibility
export const FEATURE_ELEMENT_IDS: Record<
  string,
  { elementId: string; page: TooltipPage }
> = Object.fromEntries(
  Object.entries(FEATURE_TOOLTIP_STEPS).map(([key, steps]) => [
    key,
    { elementId: steps[0].elementId, page: steps[0].page },
  ]),
);

// High-value actions that trigger deferred upgrade modal
export const HIGH_VALUE_ACTIONS = [
  'ai-summary',
  'transcript',
  'ai-chat',
  'notes',
  'youtube-summary',
  'ask-ai',
  'generate-summary',
  'view-transcript',
];

interface OnboardingData {
  howDidYouHear: string | null;
  userType: string | null;
  recordingSources: Set<string>;
  useCases: Set<string>;
}

interface OnboardingContextType {
  // State
  onboardingData: OnboardingData;
  showTooltips: boolean;
  activeTooltipFeature: string | null;
  tooltipStep: number; // Current step (0-indexed)
  hasCompletedSurvey: boolean;
  tooltipsCompleted: boolean; // True when all tooltip steps are done

  // Actions
  setHowDidYouHear: (value: string) => void;
  setUserType: (value: string) => void;
  toggleRecordingSource: (source: string) => void;
  toggleUseCase: (useCase: string) => void;
  completeSurvey: (skipped?: boolean) => void;
  advanceTooltip: () => void; // Advance to next step (button click)
  cancelTooltip: () => void; // Fully dismiss tooltip (X button, backdrop click)
  handleHighValueAction: (actionId: string, callback?: () => void) => boolean;

  // Feature flags
  isObTooltipsVariant: boolean;
  isDeferredUpgradeVariant: boolean;
  isOriginalObVariant: boolean;
}

const OnboardingContext = createContext<OnboardingContextType | null>(null);

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return context;
};

// Safe hook that returns null if not within provider
export const useOnboardingSafe = () => {
  return useContext(OnboardingContext);
};

interface OnboardingProviderProps {
  children: React.ReactNode;
}

export const OnboardingProvider: React.FC<OnboardingProviderProps> = ({
  children,
}) => {
  const dispatch = useDispatch();
  const { getFeature } = useBooleanFeature();
  const { getTag, setTag } = useUserTags();
  const { subscription } = useSelector((state: RootState) => state.plan);

  // Feature flag states
  const isOriginalObVariant = getFeature('feature_show_signup_onboarding');
  const isObTooltipsVariant = getFeature('feature_ob_tooltips');
  const isDeferredUpgradeVariant = getFeature('feature_deferred_upgrade_modal');

  // Onboarding data
  const [howDidYouHear, setHowDidYouHearState] = useState<string | null>(null);
  const [userType, setUserTypeState] = useState<string | null>(null);
  const [recordingSources, setRecordingSources] = useState<Set<string>>(
    new Set(),
  );
  const [useCases, setUseCases] = useState<Set<string>>(new Set());

  // Check if survey has been completed
  const hasCompletedSurvey = getTag('onboardingStep') === 'completed';

  // Check if tooltips have been shown
  const hasShownTooltips = getTag('ob-tooltips-shown') === 'true';

  // Check stored tooltip state (persisted for navigation)
  const storedTooltipFeature = getTag('ob-tooltip-feature') as
    | string
    | undefined;
  const storedTooltipStep = getTag('ob-tooltip-step') as string | undefined;

  // Check if tooltips are fully completed (all steps done)
  const storedTooltipsCompleted = getTag('ob-tooltips-completed') === 'true';

  // Check if deferred upgrade has been triggered
  const hasDeferredUpgradeTriggered =
    getTag('ob-deferred-upgrade-triggered') === 'true';

  // Tooltip state - initialize from stored values if available (only for tooltips variant)
  const [showTooltips, setShowTooltips] = useState(() => {
    // Only show tooltips if in tooltips variant AND there's a stored feature and not completed
    return (
      isObTooltipsVariant &&
      !!(storedTooltipFeature && !storedTooltipsCompleted)
    );
  });
  const [activeTooltipFeature, setActiveTooltipFeature] = useState<
    string | null
  >(() => (isObTooltipsVariant ? storedTooltipFeature || null : null));
  const [tooltipStep, setTooltipStep] = useState(() => {
    if (!isObTooltipsVariant) return 0;
    const step = parseInt(storedTooltipStep || '0', 10);
    return Number.isNaN(step) ? 0 : step;
  });
  const [tooltipsCompleted, setTooltipsCompleted] = useState(
    isObTooltipsVariant ? storedTooltipsCompleted : false,
  );

  // Set how did you hear
  const setHowDidYouHear = useCallback((value: string) => {
    setHowDidYouHearState(value);
    posthog.capture('onboarding_how_did_you_hear', { selection: value });
  }, []);

  // Set user type
  const setUserType = useCallback((value: string) => {
    setUserTypeState(value);
    posthog.capture('onboarding_user_type', { selection: value });
  }, []);

  // Toggle recording source
  const toggleRecordingSource = useCallback((source: string) => {
    setRecordingSources((prev) => {
      const newSources = new Set(prev);
      const isSelected = newSources.has(source);
      if (isSelected) {
        newSources.delete(source);
      } else {
        newSources.add(source);
      }
      posthog.capture('onboarding_recording_sources', {
        selection: source,
        selected: !isSelected,
        total_selected: newSources.size,
      });
      return newSources;
    });
  }, []);

  // Toggle use case
  const toggleUseCase = useCallback((useCase: string) => {
    setUseCases((prev) => {
      const newUseCases = new Set(prev);
      const isSelected = newUseCases.has(useCase);
      if (isSelected) {
        newUseCases.delete(useCase);
      } else {
        newUseCases.add(useCase);
      }
      posthog.capture('onboarding_use_cases', {
        selection: useCase,
        selected: !isSelected,
        total_selected: newUseCases.size,
      });
      return newUseCases;
    });
  }, []);

  // Complete survey - triggers tooltip or deferred modal logic
  // skipped: true if user dismissed the survey early without completing all steps
  const completeSurvey = useCallback(
    (skipped?: boolean) => {
      // Save survey data to tags
      setTag({ key: 'onboardingStep', value: 'completed' });
      setTag({
        key: 'ob-use-cases',
        value: JSON.stringify(Array.from(useCases)),
      });
      setTag({
        key: 'ob-recording-sources',
        value: JSON.stringify(Array.from(recordingSources)),
      });

      // Determine variant name for tracking
      let variantName = 'original';
      if (isObTooltipsVariant) {
        variantName = 'tooltips';
      } else if (isDeferredUpgradeVariant) {
        variantName = 'deferred_upgrade';
      }

      posthog.capture('onboarding_survey_completed', {
        variant: variantName,
        how_did_you_hear: howDidYouHear,
        user_type: userType,
        recording_sources: Array.from(recordingSources),
        use_cases: Array.from(useCases),
        skipped: !!skipped,
      });

      // For tooltips variant, determine which feature to highlight
      if (isObTooltipsVariant && !hasShownTooltips) {
        // If survey was skipped, show the generic 'all' tooltip pointing to demo
        if (skipped) {
          setActiveTooltipFeature('all');
          setShowTooltips(true);
          setTag({ key: 'ob-tooltips-shown', value: 'true' });
          setTag({ key: 'ob-tooltip-feature', value: 'all' });

          posthog.capture('onboarding_tooltip_shown', {
            feature: 'all',
            use_cases: Array.from(useCases),
            skipped: true,
          });
          return;
        }

        // Find first selected use case that maps to a feature
        const selectedUseCases = Array.from(useCases);

        // Check if "All of the above" is selected - use 'all' feature
        if (selectedUseCases.includes('All of the above')) {
          setActiveTooltipFeature('all');
          setShowTooltips(true);
          setTag({ key: 'ob-tooltips-shown', value: 'true' });
          setTag({ key: 'ob-tooltip-feature', value: 'all' });

          posthog.capture('onboarding_tooltip_shown', {
            feature: 'all',
            use_cases: Array.from(useCases),
          });
          return;
        }

        // Find the first use case that maps to a specific feature
        const matchedUseCase = selectedUseCases.find((useCase) => {
          const featureId = USE_CASE_FEATURE_MAP[useCase];
          return featureId && featureId !== 'all';
        });

        let featureToHighlight: string | null = matchedUseCase
          ? USE_CASE_FEATURE_MAP[matchedUseCase]
          : null;

        // Default to 'all' (demo video) if no specific selection
        if (!featureToHighlight) {
          featureToHighlight = 'all';
        }

        setActiveTooltipFeature(featureToHighlight);
        setShowTooltips(true);
        setTag({ key: 'ob-tooltips-shown', value: 'true' });
        setTag({ key: 'ob-tooltip-feature', value: featureToHighlight });

        posthog.capture('onboarding_tooltip_shown', {
          feature: featureToHighlight,
          use_cases: Array.from(useCases),
        });
      }
    },
    [
      setTag,
      useCases,
      recordingSources,
      howDidYouHear,
      userType,
      isObTooltipsVariant,
      isDeferredUpgradeVariant,
      hasShownTooltips,
    ],
  );

  // Advance tooltip - moves to next step or completes (button click, demo video click)
  const advanceTooltip = useCallback(() => {
    if (!activeTooltipFeature) return;

    const steps = FEATURE_TOOLTIP_STEPS[activeTooltipFeature] || [];
    const currentStepConfig = steps[tooltipStep];
    const hasNextStep = tooltipStep < steps.length - 1;

    posthog.capture('onboarding_tooltip_advanced', {
      feature: activeTooltipFeature,
      step: tooltipStep + 1,
      totalSteps: steps.length,
    });

    if (hasNextStep && currentStepConfig?.navigateToDemo) {
      // Move to next step - persist to tags for navigation survival
      const nextStep = tooltipStep + 1;
      setTooltipStep(nextStep);
      setTag({ key: 'ob-tooltip-step', value: String(nextStep) });
    } else {
      // All steps complete - dismiss tooltips and clear persisted state
      setShowTooltips(false);
      setActiveTooltipFeature(null);
      setTooltipStep(0);
      setTooltipsCompleted(true);
      setTag({ key: 'ob-tooltips-completed', value: 'true' });
      setTag({ key: 'ob-tooltip-step', value: '0' });
      setTag({ key: 'ob-tooltip-feature', value: '' });

      posthog.capture('onboarding_tooltips_completed', {
        feature: activeTooltipFeature,
      });
    }
  }, [activeTooltipFeature, tooltipStep, setTag]);

  // Cancel tooltip - fully dismisses without advancing (X button, backdrop click)
  const cancelTooltip = useCallback(() => {
    if (!activeTooltipFeature) return;

    posthog.capture('onboarding_tooltip_cancelled', {
      feature: activeTooltipFeature,
      step: tooltipStep + 1,
    });

    // Fully dismiss - no next step, mark as completed, clear persisted state
    setShowTooltips(false);
    setActiveTooltipFeature(null);
    setTooltipStep(0);
    setTooltipsCompleted(true);
    setTag({ key: 'ob-tooltips-completed', value: 'true' });
    setTag({ key: 'ob-tooltip-step', value: '0' });
    setTag({ key: 'ob-tooltip-feature', value: '' });
  }, [activeTooltipFeature, tooltipStep, setTag]);

  // Handle high-value action click - triggers upgrade modal after tooltips are complete
  // Returns true if upgrade modal was shown (action should be blocked), false otherwise
  const handleHighValueAction = useCallback(
    (actionId: string, callback?: () => void): boolean => {
      // For tooltips variant: trigger after tooltips are completed
      // For deferred upgrade variant: trigger on first high-value action (after survey)
      const shouldTriggerForTooltips =
        isObTooltipsVariant &&
        tooltipsCompleted &&
        !hasDeferredUpgradeTriggered;
      const shouldTriggerForDeferred =
        isDeferredUpgradeVariant &&
        hasCompletedSurvey &&
        !hasDeferredUpgradeTriggered;

      if (!shouldTriggerForTooltips && !shouldTriggerForDeferred) {
        if (callback) callback();
        return false;
      }

      // Check if this is a high-value action
      if (!HIGH_VALUE_ACTIONS.includes(actionId)) {
        if (callback) callback();
        return false;
      }

      // Check if user is on free plan
      const hasPastPackages = (subscription.pastPackages || []).length > 0;
      if (subscription.package !== 'FREE_LOGGEDIN' || hasPastPackages) {
        if (callback) callback();
        return false;
      }

      // Mark as triggered and show upgrade modal
      setTag({ key: 'ob-deferred-upgrade-triggered', value: 'true' });

      const source = shouldTriggerForTooltips ? 'ob_tooltip' : 'ob_deferred';
      posthog.capture('onboarding_deferred_upgrade_triggered', {
        action: actionId,
        source,
      });

      dispatch(
        pushModal({
          type: 'free-trial-modal',
          source,
        }),
      );

      // Return true to indicate modal was shown and action should be blocked
      return true;
    },
    [
      isObTooltipsVariant,
      isDeferredUpgradeVariant,
      tooltipsCompleted,
      hasCompletedSurvey,
      hasDeferredUpgradeTriggered,
      subscription,
      setTag,
      dispatch,
    ],
  );

  // Load saved use cases on mount for tooltip variant
  useEffect(() => {
    if (isObTooltipsVariant && hasCompletedSurvey && !hasShownTooltips) {
      const savedUseCases = getTag('ob-use-cases');
      if (savedUseCases) {
        try {
          const parsed = JSON.parse(savedUseCases as string);
          setUseCases(new Set(parsed));
        } catch {
          // Ignore parse errors
        }
      }
    }
  }, [isObTooltipsVariant, hasCompletedSurvey, hasShownTooltips, getTag]);

  // Reset tooltip state if not in tooltips variant (handles async flag loading)
  useEffect(() => {
    if (!isObTooltipsVariant) {
      // Clear any tooltip state for non-tooltips variants
      setShowTooltips(false);
      setActiveTooltipFeature(null);
      setTooltipStep(0);
    }
  }, [isObTooltipsVariant]);

  const contextValue = useMemo<OnboardingContextType>(
    () => ({
      onboardingData: {
        howDidYouHear,
        userType,
        recordingSources,
        useCases,
      },
      showTooltips,
      activeTooltipFeature,
      tooltipStep,
      hasCompletedSurvey,
      tooltipsCompleted,
      setHowDidYouHear,
      setUserType,
      toggleRecordingSource,
      toggleUseCase,
      completeSurvey,
      advanceTooltip,
      cancelTooltip,
      handleHighValueAction,
      isObTooltipsVariant,
      isDeferredUpgradeVariant,
      isOriginalObVariant,
    }),
    [
      howDidYouHear,
      userType,
      recordingSources,
      useCases,
      showTooltips,
      activeTooltipFeature,
      tooltipStep,
      hasCompletedSurvey,
      tooltipsCompleted,
      setHowDidYouHear,
      setUserType,
      toggleRecordingSource,
      toggleUseCase,
      completeSurvey,
      advanceTooltip,
      cancelTooltip,
      handleHighValueAction,
      isObTooltipsVariant,
      isDeferredUpgradeVariant,
      isOriginalObVariant,
    ],
  );

  return (
    <OnboardingContext.Provider value={contextValue}>
      {children}
    </OnboardingContext.Provider>
  );
};

export default OnboardingProvider;
